import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { FileText, Undo2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { inr } from "@/lib/constants";

export default function DoctorInvoices() {
    const [invoices, setInvoices] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [doctor, setDoctor] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/payments/me/invoices"),
            api.get("/me/doctor"),
        ])
            .then(([i, d]) => {
                setInvoices(i.data);
                setDoctor(d.data);
                // Flatten embedded refunds so the old refundsByOrder lookup keeps working.
                const flat = [];
                for (const inv of (i.data || [])) {
                    for (const r of (inv.refunds?.items || [])) flat.push(r);
                }
                setRefunds(flat);
            })
            .catch((err) =>
                toast.error(errMsg(err, "Failed to load"))
            );
    }, []);

    // Group refunds by order_id for quick lookup on invoice rows
    const refundsByOrder = refunds.reduce((acc, r) => {
        (acc[r.order_id] = acc[r.order_id] || []).push(r);
        return acc;
    }, {});

    return (
        <DoctorShell doctor={doctor}>
            <div className="animate-fade-up">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                    Invoices
                </div>
                <h1 className="mt-1 text-3xl font-bold text-[#123B5D]">
                    Your invoices
                </h1>
                <p className="mt-1 text-sm text-[#17212B]/60">
                    All successful payments are billed and archived here.
                </p>
                <div className="mt-8 ddi-card overflow-hidden" data-testid="invoices-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#123B5D]/5 text-[#123B5D]">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Invoice</th>
                                <th className="text-left px-4 py-3 font-semibold">Package</th>
                                <th className="text-left px-4 py-3 font-semibold">Installment</th>
                                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                                <th className="text-right px-4 py-3 font-semibold">Tax</th>
                                <th className="text-right px-4 py-3 font-semibold">Total</th>
                                <th className="text-left px-4 py-3 font-semibold">Issued</th>
                                <th className="text-left px-4 py-3 font-semibold">Gateway</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-[#17212B]/50">
                                        <FileText size={24} className="inline mb-2 text-[#17212B]/30" />
                                        <div>No invoices yet.</div>
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((inv) => {
                                    const rs = refundsByOrder[inv.order_id] || [];
                                    const refundedTotal = rs.reduce((s, r) => s + (["success", "pending"].includes(r.status) ? (r.amount_inr || 0) : 0), 0);
                                    return (
                                    <Fragment key={inv.id}>
                                    <tr className="border-t border-[#123B5D]/5" data-testid={`invoice-row-${inv.invoice_number}`}>
                                        <td className="px-4 py-3 font-mono text-[#123B5D]">
                                            {inv.invoice_number}
                                        </td>
                                        <td className="px-4 py-3">{inv.package_name}</td>
                                        <td className="px-4 py-3 capitalize">{inv.installment}</td>
                                        <td className="px-4 py-3 text-right">{inr(inv.amount_inr)}</td>
                                        <td className="px-4 py-3 text-right">{inr(inv.tax_inr)}</td>
                                        <td className="px-4 py-3 text-right font-semibold text-[#17212B]">{inr(inv.total_inr)}</td>
                                        <td className="px-4 py-3 text-[#17212B]/70">{new Date(inv.issued_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 capitalize text-[#17212B]/70">{inv.gateway}</td>
                                    </tr>
                                    {rs.map((r) => (
                                        <tr key={r.id} className="border-t border-dashed border-[#B45309]/25 bg-[#FEF9EE]" data-testid={`refund-line-${r.id}`}>
                                            <td className="px-4 py-2 text-[11px] text-[#B45309] font-mono">
                                                <Undo2 size={11} className="inline mr-1" />
                                                {r.cf_refund_id || r.refund_id}
                                            </td>
                                            <td colSpan={2} className="px-4 py-2 text-[11px] text-[#78350F]">
                                                Refund · {r.note || "Merchant initiated"}
                                            </td>
                                            <td colSpan={3} className="px-4 py-2 text-right text-[11px] text-[#B45309]">−{inr(r.amount_inr)}</td>
                                            <td className="px-4 py-2 text-[11px] text-[#78350F]">{new Date(r.created_at).toLocaleDateString()}</td>
                                            <td className="px-4 py-2 text-[11px] uppercase text-[#B45309]">{r.status}</td>
                                        </tr>
                                    ))}
                                    {refundedTotal > 0 && (
                                        <tr className="border-t border-[#123B5D]/5 bg-[#FEF9EE]/50">
                                            <td colSpan={5} className="px-4 py-2 text-[11px] text-[#78350F] italic text-right">Net after refunds</td>
                                            <td className="px-4 py-2 text-right font-semibold text-[#78350F]">{inr(inv.total_inr - refundedTotal)}</td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    )}
                                    </Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </DoctorShell>
    );
}
